import { get } from 'lodash';
import domainsService from '@clubmed/domains-service';
import { constants } from '@clubmed/utils';
import {
  activityCategoryFragment,
  activityFragment,
  rentalsFragment,
  thematicFragment,
} from 'Client/cms/toolkit/product-utils/productFeatures.queries';
import {
  getResort,
  fetchValidationSchema,
  getAttendeesData,
} from 'Views/booking/BookingEngine.query';
import type { Kids as KidsType } from 'Store/trip-information/kids/kids.reducer';
import type {
  Adults as AdultsType,
  State as ArrayAdultsType,
} from 'Store/trip-information/adults/adults.reducer';
import { profileFragment } from 'Components/widgets/contact-info/ContactInfo.fragment';
import BookingAttendeesProfile from 'Components/widgets/contact-info/BookingAttendeesProfile.fragment';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  PROPOSAL_ACCOMMODATION_ADD,
  PROPOSAL_ADD_TRANSPORT,
  PROPOSAL_GET_SCHEDULE_PAYMENT,
  PROPOSAL_GET_SEVERAL,
  PROPOSAL_USE_PROMO_CODE,
  PROPOSAL_AUTOSAVE,
  PROPOSAL_SWITCH_OFFER,
  PROPOSAL_ADDITIONAL_SERVICES_ADD,
  PROPOSAL_ADDITIONAL_SERVICES_UPDATE,
  PROPOSAL_ADDITIONAL_SERVICES_DELETE,
  PROPOSAL_REFRESH,
} from 'Utils/request/requestDictionary';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { graphqlify } from 'Utils/graphql';
const { JOYVIEW_ROOM_ONLY, JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES } = constants.PACKAGES;
type ProposalsQueryParam = {
  arrivalDate: string;
  adults: ArrayAdultsType;
  kids?: ReadonlyArray<{
    readonly birthdate: timestamp;
    readonly childcareIncluded: boolean;
    readonly neolid?: string;
  }>;
  departureCity?: string;
  duration: number;
  isJoyView?: boolean;
  isUrbanOasis?: boolean;
  numberAttendees: number;
  productId: string;
  transferCode?: boolean;
  adultsAttendeesIds?: Array<string>;
  kidsAttendeesIds?: Array<string>;
  fetchActivityCategories?: boolean;
  salesNetworkId?: string;
  voucherCode?: string;
  isPrebookingFormQuery?: boolean;
  accommodationIds?: Array<string>;
};
const paymentScheduleRequest = `paymentSchedule {
  commissionIncluded
  total {...priceDetails}
  depositAmount {...priceDetails}
  repaymentSchedules{
    amount {...priceDetails}
    deadline
  }
}`;

function getPaymentScheduleRequest(appContext: AppContext): string {
  return isFeatureFlipped(appContext)('booking.deposit') ? paymentScheduleRequest : '';
}

function getPriceDetailFragment(locale: string): string {
  const precision = domainsService.getBookingPrecision(locale);
  return `
    fragment priceDetails on PriceDetailType {
      formattedValue(precision : ${precision})
      value
    }
  `;
}

const flightFragment = `{
  id
  cityId
  label
  airportLabel
  terminalCode
  date
  time
}`;
const transportFragment = `{
  type
  class
  travelNumber
  baggage {
    quantity
    weight
    unit
  }
  companyOperator {
    id
    label
    logo
  }
  companyExploiter {
    id
    label
    logo
  }
  vehicleType
  carbonEmission
  averageCarbonEmission
}`;
const transportDetailsFragment = `
  fragment transportsDetails on TransportDetailType {
    isClubmedTransport
    durationInMin
    travelSections {
      connectionDurationInMin
      isClubmedTransport
      stopDurationInMin
      technicalStops
      departure {
        ...${flightFragment}
      }
      arrival {
        ...${flightFragment}
      }
      transport {
        ...${transportFragment}
      }
    }
    cancellationPolicyType
    transferIncluded
    stayId
  }
`;
export const servicesWithSchedulePricingInfoQuery = `
    pricingInfo {
        id
        progId
        type
        currency
        rentedAmount {...priceDetails, currency}
        rentedAmountWithoutDiscount {...priceDetails, currency}
        startingPricePerAdult {
          ...priceDetails
          currency
          discount
          discountUnit
          formattedInitialValue
        }
        isPartiallyRented
        attendees {
          id
          rentedAmount {
            ...priceDetails
            currency
          }
          rentedAmountWithoutDiscount {
            ...priceDetails
            currency
          }
          price {
            discount
            discountUnit
            formattedValue(precision:0)
            formattedInitialValue
            value
          }
          schedules {
            startDate
            endDate
            isPartiallyRented
            isPartiallyPaid
            isFullyRented
            isFullyPaid
            rentedAmount {...priceDetails}
            timeSlots {
              id
              slotName
              remainingStock
              isRented
              isPaid
              price {
                ...priceDetails
                currency
                discount
                discountUnit
                formattedInitialValue
              }
            }
          }
        }
        schedules {
          startDate
          endDate
          slots {
            id
            type
            initialStock
            remainingStock
            attendees {
              id
              price {
                ...priceDetails
              }
            }
          }
        }
      }`;

/**
 * childcare with schedule and childId
 */
export const childcareWithSchedulePricingInfoQuery = `
    pricingInfo {
        id
        progId
        childId
        type
        currency
        rentedAmount {...priceDetails, currency}
        startingPricePerAdult {
          ...priceDetails
          currency
          discount
          discountUnit
          formattedInitialValue
        }
        isPartiallyRented
        attendees {
          id
          rentedAmount {...priceDetails currency}
          price {
            discount
            discountUnit
            formattedValue(precision:0)
            formattedInitialValue
            value
          }
          schedules {
            startDate
            endDate
            isPartiallyRented
            isPartiallyPaid
            isFullyRented
            isFullyPaid
            rentedAmount {...priceDetails}
            timeSlots {
              id
              slotName
              remainingStock
              isRented
              isPaid
              price {
                ...priceDetails
                currency
                discount
                discountUnit
                formattedInitialValue
              }
            }
          }
        }
        schedules {
          startDate
          endDate
          slots {
            id
            type
            initialStock
            remainingStock
            attendees {
              id
              price {
                ...priceDetails
              }
            }
          }
        }
      }`;
export const childcaresQuery = `
childcares {
  ${childcareWithSchedulePricingInfoQuery}
  productInfo {
    id
    label
    images
    description
    durationDescription
    isIncluded
    ageInMonths {
      min
      max
    }
    closingWeekdays
    openingHours {
      start
      end
    }
    timeSlots {
      type
      opening
      closing
    }
  }
}`;
export const transfersQuery = `
transfers {
  pricingInfo {
    id
    isIncluded
    price: startingPricePerAdult {
      ...priceDetails
      formattedValueRounded : formattedValue(precision : 0)
    }
    totalPrice {
      value
      formattedValue
      currency
    }
    isPartiallyRented
    isFullyRented
    type
    rentedAmount {
      value
    }
    schedules {
      startDate
      endDate
      remainingStock
      attendees {
        id
        isRented
      }
    }
  }
  productInfo {
    id
    description
    durationInMinutes
    vehicle {
      id
      label
    }
    label
  }
  directionType
}
`;
export const insuranceAdditionalServicesQuery = `insurance {
        pricingInfo {
          id
          type
          rentedAmount {...priceDetails, currency}
          totalPrice {
            value
            formattedValue(precision: 2)
            currency
          }
          isPartiallyRented
          isPartiallyPaid
          isFullyPaid
          schedules {
            startDate
            endDate
            attendees {
              id
            }
          }
        }
        productInfo {
          id
          label
          shortDescription
          insuranceDetails {
            id
            label
            images
            description
            shortDescription
            videos
            attachedDocuments {
              label
              url
            }
            company {
              label
              logo
            }
            coverageDescriptions
            subscriptionConditions {
              age_min
              ageMax
              byAttendees
            }
          }
        }
      }`;
const additionalServicesQuery = `
  additionalServices(types: [EXCURSION, RENTAL, CHILDCARE, LAND_SPORT, WATER_SPORT, WINTER_SPORT, TRANSFER, WELLNESS, INSURANCE]) {
    activities {
      ${servicesWithSchedulePricingInfoQuery}
      productInfo {
        id
        label
        isIncluded
        parentActivityCategoriesId
        description
      }
    }
    rentals {
      ${servicesWithSchedulePricingInfoQuery}
      productInfo {
        id
        label
        ageInMonths {
          min
          max
        }
        useCase {
          id
          label
        }
        typeId: type
        typeLabel
        description: longDescription
        sizes {
          min
          max
          unit
        }
        practiceLevels {
          id
          label
        }
      }
    }
    ${childcaresQuery}
    ${transfersQuery}
    ${insuranceAdditionalServicesQuery}
  }
  `;

function proposalPriceQuery(): string {
  return `
    price {
      total {...priceDetails}
      totalWithoutDiscount {...priceDetails}
      aggregateDiscount {
        offerColor
        offerName
        amount {...priceDetails}
      }
      aggregateDiscountPromoCode {
        offerColor
        offerName
        amount {...priceDetails}
      }
      promoCode {
        offerColor
        offerId
        offerName
        amount {
          value
          formattedValue
        }
      }
      totalWithoutChildcare {...priceDetails}
      totalWithoutChildcareAndDiscount {...priceDetails}
      insurance {...priceDetails}
      totalWithoutTransport {...priceDetails}
      initialPrice {...priceDetails}
      discounts {
        amount {...priceDetails}
        offerId
        offerName
        offerColor
        offerDescription
        offerLabel
        offerType
        currentProposalCode {
          id
          label
          description
          value
          unit
        }
      }
      transfer {...priceDetails}
      fees {
        type
        amount {...priceDetails}
      }
      childcare {...priceDetails}
      rental {...priceDetails}
      excursions {...priceDetails}
      wellness {...priceDetails}
      sports {...priceDetails}
      currency
      hasFlexRates
      selectedRate
      yieldPricingVariation
      gap
    }
    alternativePrice {
      total {...priceDetails}
      totalWithoutDiscount {...priceDetails}
      aggregateDiscount {
        offerColor
        offerName
        amount {...priceDetails}
      }
      aggregateDiscountPromoCode {
        offerColor
        offerName
        amount {...priceDetails}
      }
      promoCode {
        offerColor
        offerId
        offerName
        amount {
          value
          formattedValue
        }
      }
      totalWithoutChildcare {...priceDetails}
      totalWithoutChildcareAndDiscount {...priceDetails}
      initialPrice {...priceDetails}
      discounts {
        amount {...priceDetails}
        offerId
        offerName
        offerColor
        offerDescription
        offerLabel
        offerType
        currentProposalCode {
          id
          label
          description
          value
          unit
        }
      }
      transfer {...priceDetails}
      insurance {...priceDetails}
      fees {
        type
        amount {...priceDetails}
      }
      childcare {...priceDetails}
      rental {...priceDetails}
      currency
    }
    attendeesPrice {
      id
      householdId
      total {
        ...priceDetails
        currency
      }
      priceItems {
        type
        offerId
        price {
          ...priceDetails
          currency
        }
      }
    }
    `;
}

function proposalFields(
  params: {
    isJoyView?: boolean;
    isUrbanOasis?: boolean;
    withCommission: boolean;
    withSchedule?: boolean;
  },
  appContext: AppContext,
) {
  return `
    id
    packageId
    productId
    originalLocale
    greatMembersLoyaltyPoints
    ${additionalServicesQuery}
    ${
      params.isJoyView || params.isUrbanOasis
        ? `
    alternativePackages {
      proposalId
      packageId
      upsellPrice
    }
    packages(packageIds : ["${JOYVIEW_ROOM_ONLY}", "${JOYVIEW_ALL_INCLUSIVE}", "${JOYVIEW_FOOD_AND_BEVERAGES}"]) {
      id
      image
      additionalInfo
      description
      label
      prices {
        perAdultPerNight {
          ...priceDetails
          currency
        }
        perKidPerNight {
          ...priceDetails
          currency
        }
      }
    }`
        : ''
    }
    label
    accommodationsCount
    persistenceExtended
    accommodations {
      id
      label
      description
      quantity
      connectingRoom
      familyRoom
      surface
      view {
        id
        label
      }
      strengths {
        id
        label
      }
      equipments {
        id
        label
      }
      services {
        id
        label
      }
      outsideFacilities {
        id
        label
      }
      bathroomEquipments {
        id
        label
      }
      equipments {
        id
        label
      }
      categories {
        id
      }
      exclusiveCollection
      floorPlans
      category
      categoryId
      comfortType
      comfortId
      maxCapacity
      media {
        photos {
          url
        }
      }
      roomType
      attendeesDistribution {
        attendees {
          id
          type
          birthdate,
          customerId
        }
      }
      ranking
    }
    duration
    media {
      photos {
        url
      }
    }
    ${proposalPriceQuery()}
    arrivalDate
    departureDate
    attendees {
      list {
        id
        birthdate
        customerId
        email
        civility
        firstName
        lastName
        gmNumber
        optins {
          channel
          optin
        }
      }
      adults {
        id
        customerId
      }
      children(birthdateSort: asc) {
        id
        birthdate
        customerId
      }
    }
    households(withSchedule: ${(!!params.withSchedule).toString()}) {
      attendees {
        id
        customerId
        birthdate
        isAdult
        isMain
      }
      ${getPaymentScheduleRequest(appContext)}
    }
    remainingStock
    optionAvailable
    isCounterProposal
    transports {
      inbound {
        ...transportsDetails
      }
      outbound {
        ...transportsDetails
      }
    }
    expirationDateTime
    durationDisclaimer
    allInclusiveServices {
      icon
      label
      description
    }
    ${
      params.withCommission
        ? `sellerCommission {
            formattedValue(precision: 2)
          }`
        : ''
    }
     `;
}

const paymentProvidersRequest = () => `paymentProviders {
  description
  id
  selectorId
  label
  logo
  categoryPaymentMethod
  shouldSkipPayment
  withBillingAddress
  paymentMethods {
    category
    id
    image
    label
    timePaymentConditions {
      chargePercentage
      id
      paymentCount
    }
  }
  requiredDelayBeforeDeparture
  token {
    format
    value
  }
}`;

function fetchPayment(proposalId: string, appContext: AppContext): Promise<any> {
  return graphQLClient.runNoCacheQuery(
    `
    {
      proposal(id: "${proposalId}") {
        households(withSchedule: true) {
          ${getPaymentScheduleRequest(appContext)}
        }
      }
      ${paymentProvidersRequest()}
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
  `,
    appContext,
    {
      requestName: PROPOSAL_GET_SCHEDULE_PAYMENT,
    },
  );
}

type Attendee = KidsType | AdultsType;

function getKnownAttendeesInformation(
  attendees: Array<Attendee>,
  neolaneId: string | null | undefined,
  travelCompanionContext: boolean = false,
): string {
  const attendeeInfo = attendees.map((attendee, index) => {
    const { neolid = '', birthdate } = attendee;
    const customerId = index === 0 && neolaneId ? neolaneId : neolid;
    // neolid : Send by CXO in query params
    // neolaneId : In appContext, identify user logged
    // When not seller, set first adult with neolaneId if user is authenticated
    // adding connected user infos creates 2 households for travel companions context which should always only have one household
    return {
      customer_id: travelCompanionContext ? undefined : customerId,
      ...(birthdate
        ? {
            birthdate: timestampToApiDate(birthdate),
          }
        : null),
    };
  });
  return graphqlify(attendeeInfo);
}

async function proposalsQuery(params: ProposalsQueryParam, appContext: AppContext): Promise<any> {
  const { adults, kids = [] } = params;
  const attendees = adults.concat(kids || []);
  // When quick logged neolaneId but should not call profile api
  // TODO: handle quicklogged status and not call profile API every fucking time because 401 is silent
  const neolaneId = appContext.isUserLogged ? appContext.neolaneId : null;
  const travelCompanionContext =
    !appContext.isSeller &&
    appContext.isUserLogged &&
    isFeatureFlipped(appContext)('booking.travelCompanions');
  const knownAttendeesInformation = getKnownAttendeesInformation(
    attendees,
    neolaneId,
    travelCompanionContext,
  );
  const displayCommission = isFeatureFlipped(appContext)('booking.displayCommission');
  const displayDurationsCounterProposals = isFeatureFlipped(appContext)(
    'booking.displayDurationsCounterProposals',
  ).toString();
  const displayCounterExtendedDurations = isFeatureFlipped(appContext)(
    'booking.displayExtendedDurationsCounterProposals',
  ).toString();
  const displayCounterDates = isFeatureFlipped(appContext)(
    'booking.displayDateCounterProposals',
  ).toString();
  const proposalArgs = [
    `productId: "${params.productId}"`,
    `arrivalDate: "${params.arrivalDate}"`,
    `duration: ${params.duration}`,
    `numberAttendees: ${params.numberAttendees}`,
    `knownAttendeesInformation: ${knownAttendeesInformation}`,
    ...(params.departureCity ? [`departureCity : "${params.departureCity}"`] : []),
    ...(params.isJoyView ? ['isJoyView: true'] : []),
    ...(params.isUrbanOasis ? ['isUrbanOasis: true'] : []),
    ...(params.salesNetworkId ? [`salesNetworkId: "${params.salesNetworkId}"`] : []),
    ...(params.voucherCode ? [`voucherCode: "${params.voucherCode}"`] : []),
    ...(params.isPrebookingFormQuery ? ['isPrebookingFormQuery: true'] : []),
    ...(params.accommodationIds
      ? [`accommodationIds : ${graphqlify(params.accommodationIds)}`]
      : []),
    `displayCounterDurations: ${displayDurationsCounterProposals}`,
    `displayCounterExtendedDurations: ${displayCounterExtendedDurations}`,
    `displayCounterDates: ${displayCounterDates}`,
  ];
  const proposalsResponse = await graphQLClient.runNoCacheQuery(
    `
    {
      proposals(${proposalArgs.join(', ')}) {
        ... on ErrorsProposalType {
          errors {
            errorCode
          }
        }
        ... on ProposalsType {
          proposals {
            ${proposalFields(
              {
                ...params,
                withCommission:
                  (appContext.isCmta || appContext.isIndirectCxo) && displayCommission,
              },
              appContext,
            )}
          }
          dateSuggestion {
            dateOffset
            price {
              total
            }
            resortArrivalDate
            resortDepartureDate
          }
          priceCurrency
          suggestions {
            resort {
              badgeIcon
              color
              comfort
              comfortLabel
              destination {
                country
              }
              hasECSpace
              href
              id
              images
              isExplicitEC
              isUrbanOasis
              name
              type
            }
            remainingStock
            initialStock
          }
          durationSuggestions {
            ids
            data {
              price {
                total {
                  formattedValue
                }
              }
              duration
              resortArrivalDate: arrivalDate
              resortDepartureDate: departureDate
            }
          }
          ${getResort(params.fetchActivityCategories)}
        }
      }
      ${appContext.isUserLogged ? 'profile { ...BookingProfile }' : ''}
      ${getAttendeesData(params as Parameters<typeof getAttendeesData>[0])}
    }

    ${params.fetchActivityCategories ? activityCategoryFragment : ''}
    ${params.fetchActivityCategories || thematicFragment ? activityFragment : ''}
    ${params.fetchActivityCategories ? rentalsFragment : ''}
    ${thematicFragment}
    ${appContext.isUserLogged ? profileFragment : ''}
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    ${params.adultsAttendeesIds?.[0] || params.kidsAttendeesIds?.[0] ? BookingAttendeesProfile : ''}
    `,
    appContext,
    {
      requestName: PROPOSAL_GET_SEVERAL,
    },
  );
  const validationSchema = proposalsResponse?.proposals?.proposals?.length
    ? await fetchValidationSchema(
        proposalsResponse?.proposals?.proposals?.[0]?.id,
        appContext,
        'profile',
      )
    : {};
  return { ...proposalsResponse, ...validationSchema };
}

function addTransportMutation(
  transportId: any,
  proposalsId: Array<string>,
  isJoyView: boolean,
  isUrbanOasis: boolean,
  appContext: AppContext,
): Promise<any> {
  const displayCommission = isFeatureFlipped(appContext)('booking.displayCommission');
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      addTransport (proposalsId: ["${proposalsId.join('","')}"], transportId: "${transportId}") {
        ${proposalFields(
          {
            isJoyView,
            isUrbanOasis,
            withCommission: (appContext.isCmta || appContext.isIndirectCxo) && displayCommission,
          },
          appContext,
        )}
      }
    }
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    {
      requestName: PROPOSAL_ADD_TRANSPORT,
    },
  );
}

function addAccommodationMutation(
  arrangement: AccommodationArrangement,
  proposalsId: Array<string>,
  isJoyView: boolean,
  isUrbanOasis: boolean,
  appContext: AppContext,
): Promise<any> {
  const displayCommission = isFeatureFlipped(appContext)('booking.displayCommission');
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      addAccommodation (proposalsId: ["${proposalsId.join('","')}"], arrangement: ${graphqlify(arrangement)}) {
        ${proposalFields(
          {
            isJoyView,
            isUrbanOasis,
            withCommission: (appContext.isCmta || appContext.isIndirectCxo) && displayCommission,
          },
          appContext,
        )}
      }
    }
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    {
      requestName: PROPOSAL_ACCOMMODATION_ADD,
    },
  );
}

export type ServicesMutationType = Readonly<{
  id: string;
  schedules: ReadonlyArray<{
    startDate: string;
    endDate: string;
    attendees: ReadonlyArray<{
      id: string;
    }>;
  }>;
}>;
export type SingleServiceMutationAttendeeType = {
  customerId: string;
  productInfoId: string;
  schedules: ReadonlyArray<{
    startDate: string;
    endDate: string;
    slots: ReadonlyArray<string>;
  }>;
};
export type ServiceMutationType = ReadonlyArray<SingleServiceMutationAttendeeType>;

const putServicesGraphQLQuery = (
  proposalsId: ReadonlyArray<string>,
  services: ServiceMutationType,
  appContext: AppContext,
) => {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      putServices(proposalIds: ${graphqlify(proposalsId)}, attendees: ${graphqlify(services)}) {
        id
        greatMembersLoyaltyPoints
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    {
      requestName: PROPOSAL_ADDITIONAL_SERVICES_UPDATE,
    },
  );
};

/**
 * We don't want to have this request in parallel, it's a put and it can be really long, so we create a queue to do
 * these requests synchronously
 */
let serviceRequestRunning = null;

const putServicesMutation = async (
  proposalIds: ReadonlyArray<string>,
  attendees: ServiceMutationType,
  appContext: AppContext,
) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = putServicesGraphQLQuery(proposalIds, attendees, appContext);
    let result;

    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;
  return putServicesMutation(proposalIds, attendees, appContext);
};

export type OverloadForSpecificProposalIdsType = Readonly<{
  proposalIds: ReadonlyArray<string>;
  serviceIds: ReadonlyArray<string>;
}>;

const deleteServiceGraphQLQuery = ({
  proposalIds,
  serviceIds,
  attendeeIds,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: ReadonlyArray<string>;
  serviceIds: ReadonlyArray<string>;
  attendeeIds: ReadonlyArray<string> | string;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
  appContext: AppContext;
}) =>
  graphQLClient.runNoCacheQuery(
    `
    mutation {
      deleteService(proposalIds: ${graphqlify(proposalIds)} serviceIds: ${graphqlify(serviceIds)} attendeeIds: ${graphqlify(attendeeIds)} ${overloadForSpecificProposalIds ? `overloadForSpecificProposalIds: ${graphqlify(overloadForSpecificProposalIds)}` : ''}) {
        id
        greatMembersLoyaltyPoints
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    {
      requestName: PROPOSAL_ADDITIONAL_SERVICES_DELETE,
    },
  );

const deleteServicesMutation = async ({
  proposalIds,
  serviceIds,
  attendeeIds,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: ReadonlyArray<string>;
  serviceIds: ReadonlyArray<string>;
  attendeeIds: ReadonlyArray<string> | string;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
  appContext: AppContext;
}) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = deleteServiceGraphQLQuery({
      proposalIds,
      serviceIds,
      attendeeIds,
      overloadForSpecificProposalIds,
      appContext,
    });
    let result;

    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;
  return deleteServicesMutation({
    proposalIds,
    serviceIds,
    attendeeIds,
    overloadForSpecificProposalIds,
    appContext,
  });
};

const addServiceGraphQLQuery = ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: ReadonlyArray<string>;
  serviceId: string;
  attendeeIds: ReadonlyArray<string> | string;
  schedules: ReadonlyArray<{
    startDate: string;
    endDate: string;
  }>;
  overloadForSpecificProposalIds?: Readonly<{
    proposalIds: ReadonlyArray<string>;
    serviceId: string;
  }>;
  appContext: AppContext;
}) =>
  graphQLClient.runNoCacheQuery(
    `
    mutation {
      addService(proposalIds: ${graphqlify(proposalIds)}, serviceId: "${serviceId}", attendeeIds: ${graphqlify(attendeeIds)}, schedules: ${graphqlify(schedules)}  ${overloadForSpecificProposalIds ? `overloadForSpecificProposalIds: ${graphqlify(overloadForSpecificProposalIds)}` : ''}) {
        id
        greatMembersLoyaltyPoints
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    {
      requestName: PROPOSAL_ADDITIONAL_SERVICES_ADD,
    },
  );

const addServiceMutation = async ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: ReadonlyArray<string>;
  serviceId: string;
  attendeeIds: ReadonlyArray<string> | string;
  schedules: ReadonlyArray<{
    startDate: string;
    endDate: string;
  }>;
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType;
  appContext: AppContext;
}) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = addServiceGraphQLQuery({
      proposalIds,
      serviceId,
      attendeeIds,
      schedules,
      ...(overloadForSpecificProposalIds && {
        overloadForSpecificProposalIds: {
          proposalIds: overloadForSpecificProposalIds.proposalIds,
          serviceId: overloadForSpecificProposalIds.serviceIds[0],
        },
      }),
      appContext,
    });
    let result;

    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;
  return addServiceMutation({
    proposalIds,
    serviceId,
    attendeeIds,
    schedules,
    overloadForSpecificProposalIds,
    appContext,
  });
};

type PromoCodeParams = {
  proposalId: string;
  promoCode: string;
  type: 'apply' | 'remove';
  partnerId?: string;
  accessCode?: string;
  customerId?: number;
};

function promoCodeProposalMutation(params: PromoCodeParams, appContext: AppContext) {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      promoCodeProposal(type: "${params.type}", id: "${params.proposalId}", promoCode: "${params.promoCode}" ${params.partnerId && params.accessCode ? `, partnerId: "${params.partnerId}", accessCode: "${params.accessCode}"` : ''} ${params.customerId && params.type === 'apply' ? `, customerId: ${params.customerId}` : ''}) {
        id
        price {
          aggregateDiscount {
            offerColor
            offerName
            amount {...priceDetails}
          }
          aggregateDiscountPromoCode {
            offerColor
            offerName
            amount {...priceDetails}
         }
          promoCode {
            offerColor
            offerName
            amount {
              value
              formattedValue
            }
          }
          total {...priceDetails}
          discounts {
            offerId
            amount {...priceDetails}
            offerName
            offerColor
            offerDescription
            offerLabel
            offerType
            currentProposalCode {
              id
              label
              description
              value
              unit
            }
          }
        }
        attendeesPrice {
          id
          householdId
          total {
            ...priceDetails
            currency
          }
          priceItems {
            type
            offerId
            price {
              ...priceDetails
              currency
            }
          }
        }
        households {
          attendees {
            id
            customerId
            birthdate
            isAdult
            isMain
          }
          paymentSchedule {
            commissionIncluded
            total {...priceDetails}
            depositAmount {...priceDetails}
            repaymentSchedules{
              amount {...priceDetails}
              deadline
            }
          }
        }
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    {
      requestName: PROPOSAL_USE_PROMO_CODE,
    },
  );
}

type AutosaveParams = {
  proposalId: string;
};

function autosaveProposalMutation(params: AutosaveParams, appContext: AppContext) {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      autosaveProposal(id: "${params.proposalId}") {
        id
        }
    }
    `,
    appContext,
    {
      requestName: PROPOSAL_AUTOSAVE,
    },
  );
}

function switchProposalsOffers(
  proposalIds: Array<string>,
  offerIds: Array<string>,
  appContext: AppContext,
) {
  return graphQLClient.runNoCacheQuery(
    `mutation {
      proposals: switchProposalsOffers(proposalIds: ["${proposalIds.join('","')}"], offerIds: ["${offerIds.join('","')}"]) {
        id
        greatMembersLoyaltyPoints
        optionAvailable
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    {
      requestName: PROPOSAL_SWITCH_OFFER,
    },
  );
}

function refreshProposal(
  proposalId: string,
  salesNetworkId: string | undefined,
  appContext: AppContext,
) {
  const graphqlParams = `id: "${proposalId}"${salesNetworkId ? `, salesNetworkId: "${salesNetworkId}"` : ''}`;
  return graphQLClient.runNoCacheQuery(
    `
    query {
      refreshProposal (${graphqlParams}) {
        proposal {
          id
          productId
          optionAvailable
          optionDurability {
            expirationDateTime
            isReliable
          },
          duration
          extendPersistence
          price {
            ...refreshProposalPrice
          }
          remainingStock
          households {
            attendees {
              id
              customerId
              customerType
              birthdate
            }
          }
          transportationSummary {
            way
            departureCity
            arrivalCity
            transportationType
            cancellationPolicyType
            clubmedTransport
            departureDate
            arrivalDate
          }
          isBookable
          creationDateTime
          packageId
          resortArrivalDate
          resortDepartureDate
          accommodations {
            id
            quantity
            sharedRoom
          }
          label
          locale
          alternativePrice {
            ...refreshProposalPrice
          }
        }
        errors {
          errorKey
          errorValue
          errorCode
          errorDescription
        }
      }
    }
    fragment refreshProposalPrice on RefreshProposalPriceType {
      currency
      total {...priceDetails}
      totalWithoutFees {...priceDetails}
      totalWithoutDiscount {...priceDetails}
      totalWithoutTransport {...priceDetails}
      discounts {
        type
        amount {...priceDetails}
        offerId
        currentProposalCode {
          id
          label
          description
          value
          unit
        }
      }
      yieldPricingVariation {...priceDetails}
      trip {
        initialPrice {...priceDetails}
        initialPriceWithFees {...priceDetails}
        fees {
          type
          amount {...priceDetails}
        }
      }
      includedServices {
        id
        amount {...priceDetails}
      }
      packageOptions {
        type
        amount {...priceDetails}
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    {
      requestName: PROPOSAL_REFRESH,
    },
  );
}

export {
  additionalServicesQuery,
  addAccommodationMutation,
  addServiceMutation,
  addTransportMutation,
  deleteServicesMutation,
  fetchPayment,
  getPaymentScheduleRequest,
  getPriceDetailFragment,
  proposalsQuery,
  proposalFields,
  proposalPriceQuery,
  transportDetailsFragment,
  paymentProvidersRequest,
  getKnownAttendeesInformation,
  promoCodeProposalMutation,
  autosaveProposalMutation,
  putServicesMutation,
  switchProposalsOffers,
  refreshProposal,
};
